import React, { useEffect, useState } from "react";

import "./reactivate-modal.css";

import {
  Button,
  Modal,
  Layout,
  Text,
  Padding,
  Container,
  Icon,
  IconList,
} from "@kidslivesafe/sc-react";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  selectAccountType,
  selectMemberData,
  selectMemberExpired,
} from "../../features/authentication/authenticationSlice";

import {
  selectPauseDetails,
  selectPrimarySubscription,
} from "../../features/subscriptions/subscriptionSlice";

import { useLocation, useNavigate } from "react-router-dom";

import { selectBrandInfo } from "../../features/brand/brandSlice";

import {
  createCustomerRedisTokenAsync,
  selectAdminStatus,
  sendMemberEventAsync,
} from "../../features/admin/adminSlice";

import { useReactivateCheckHook } from "../../hooks/reactivateHook";

const ReactivateModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const reactivateCheck = useReactivateCheckHook();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [reactivateCheckFired, setReactivateCheckFired] =
    useState<boolean>(false);

  const brandInfo = useAppSelector(selectBrandInfo);
  const adminStatus = useAppSelector(selectAdminStatus);
  const memberExpired = useAppSelector(selectMemberExpired);
  const pauseDetails = useAppSelector(selectPauseDetails);
  const accountType = useAppSelector(selectAccountType);
  const primarySubscription = useAppSelector(selectPrimarySubscription);
  const memberData = useAppSelector(selectMemberData);

  useEffect(() => {
    if (adminStatus === "loading") {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [adminStatus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramValue = params.get("reactivated");
    if (paramValue === "true" && !reactivateCheckFired) {
      setReactivateCheckFired(true);
      reactivateCheck();
    }
  }, [location.search, reactivateCheck, reactivateCheckFired]);

  const handleReactivateMembership = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );
    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token; // TODO: remove this after next PDC funnel deploy (after 08/05/2025)
      window.location.href = `${brandInfo.homeUrl}/reactivate?scn=member-reactivate&uuid=${uuid}${
        accountType === "people" || accountType === "property"
          ? `&sku=${primarySubscription?.productSku}`
          : ""
      }`;
    }
    //TODO: display message if redis token setting fails? not sure
  };

  useEffect(() => {
    if (memberExpired) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Modal Display",
          EventAction: "Modal Displays",
          MetaData: JSON.stringify({
            key: "Reactivation",
            value: "Modal",
          }),
        }),
      );
    }
  }, [memberExpired]);

  return (
    <Modal open={memberExpired} setOpen={() => {}} size="fit-content">
      <Padding top="xs" bottom="xs">
        <Layout
          type="flex"
          flexProps={{
            direction: "column",
            gap: "lg",
            alignItems: "center",
          }}
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "xxs",
              alignItems: "center",
            }}
          >
            <Text tag="h3">Hi {memberData.firstname},</Text>
            <Text tag="h2">Welcome back!</Text>
          </Layout>
          <Container
            altColor="grey"
            border
            bodyBorderRadius
            customClassName="dashboard_reactivate-modal"
          >
            <Padding space="md" top="lg" bottom="lg">
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  alignItems: "center",
                  gap: "md",
                }}
              >
                <Text size="sm" fontWeight="semibold">
                  Reactivate for instant access to:
                </Text>
                <Container altColor="grey">
                  <Layout
                    type="flex"
                    flexProps={{ gap: "md", direction: "column" }}
                  >
                    <Layout
                      type="flex"
                      flexProps={{ gap: "xs", alignItems: "center" }}
                    >
                      <Text tag="div" color="success">
                        <Icon name={IconList.IconYes} size={16} />
                      </Text>
                      <Text size="sm">350+ million people records</Text>
                    </Layout>
                    <Layout
                      type="flex"
                      flexProps={{ gap: "xs", alignItems: "center" }}
                    >
                      <Text tag="div" color="success">
                        <Icon name={IconList.IconYes} size={16} />
                      </Text>
                      <Text size="sm">160+ million property records</Text>
                    </Layout>
                    <Layout
                      type="flex"
                      flexProps={{ gap: "xs", alignItems: "center" }}
                    >
                      <Text tag="div" color="success">
                        <Icon name={IconList.IconYes} size={16} />
                      </Text>
                      <Text size="sm">500+ million phone records</Text>
                    </Layout>
                  </Layout>
                </Container>
                <Container altColor="mid-grey" bodyBorderRadius>
                  <Padding space="xs">
                    <Text fontWeight="semibold" size="xs">
                      All organized into easy-to-read reports
                    </Text>
                  </Padding>
                </Container>
              </Layout>
            </Padding>
          </Container>
          <Layout type="flex" flexProps={{ direction: "column", gap: "xs" }}>
            <Button
              buttonText="View My Reactivation Offer"
              handleClick={
                pauseDetails && pauseDetails.isCurrentlyPaused
                  ? () => navigate("/account-paused")
                  : () => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Reactivation Offer",
                          MetaData: JSON.stringify({
                            key: "Reactivation",
                            value: "Offer CTA",
                          }),
                        }),
                      );
                      handleReactivateMembership();
                    }
              }
              clicked={loading}
              loading={loading}
              fill="fill"
            />
            <Text textAlign="center" size="xs">
              Click “View My Reactivation Offer” to see your personalized
              pricing and membership options.
            </Text>
          </Layout>
        </Layout>
      </Padding>
    </Modal>
  );
};

export default ReactivateModal;
